import { useCallback, useEffect, useState } from "react";

import {
  AiBudget,
  AiBudgetAlert,
  aiGatewayService,
} from "services/aiGatewayService";
import {
  CustomerCompany,
  customerCompanyService,
} from "services/customerCompanyService";
import { WidgetConfig, widgetService } from "services/widgetService";

/** How many alerts the panel shows. The endpoint clamps to 1–200. */
const ALERT_LIMIT = 25;

/** The scope picker lists active customer companies; a workspace with more than this is not a picker's job. */
const CUSTOMER_COMPANY_PAGE = 200;

/**
 * Loads budgets, their recent alerts, and the widget and customer-company lists the budget form
 * scopes against (WP-70 added the customer-company scope).
 *
 * They are fetched together because they are read together: a budget row renders its widget's
 * name, and the alert table is only meaningful next to the budgets that produced it. Like
 * `useAiCosts`, this stays out of `useAiGateway` so it only runs when the Budgets tab is opened.
 *
 * `refreshToken` is what the page's Refresh button changes — spend and alerts move without any
 * edit on this page, so re-reading them is the whole point of the button on this tab.
 *
 * The axios interceptor already fires an error toast on failure; `error` here only drives the
 * inline empty/error state.
 */
export function useAiBudgets(refreshToken = 0) {
  const [budgets, setBudgets] = useState<AiBudget[]>([]);
  const [alerts, setAlerts] = useState<AiBudgetAlert[]>([]);
  const [widgets, setWidgets] = useState<WidgetConfig[]>([]);
  const [customerCompanies, setCustomerCompanies] = useState<CustomerCompany[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [loadedBudgets, loadedAlerts, loadedWidgets, loadedCompanies] =
        await Promise.all([
          aiGatewayService.getBudgets(),
          aiGatewayService.getBudgetAlerts(ALERT_LIMIT),
          widgetService.getAllWidgets(),
          customerCompanyService.getAll({ pageSize: CUSTOMER_COMPANY_PAGE }),
        ]);
      setBudgets(loadedBudgets);
      setAlerts(loadedAlerts);
      setWidgets(loadedWidgets);
      setCustomerCompanies(loadedCompanies.items);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "load-failed");
    } finally {
      setLoading(false);
    }
    // `refreshToken` is not read here — it is the dependency that re-runs the same reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  useEffect(() => {
    reload();
  }, [reload]);

  return {
    budgets,
    alerts,
    widgets,
    customerCompanies,
    loading,
    error,
    reload,
  };
}
